import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Alert,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Monitor as MonitorIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  NetworkCheck as NetworkIcon,
  CheckCircle,
  ShoppingCart as ShoppingCartIcon,
  AddBusiness as AddBusinessIcon,
} from "@mui/icons-material";
import { DashboardLayout } from "../../../app/layout/DashboardLayout";
import { useAdmin } from "../hooks/useAdmin";
import { CompanyManagement } from "../components/CompanyManagement";
import { UserManagement } from "../components/UserManagement";
import { SystemSettingsComponent } from "../components/SystemSettings";
import { CompanyRegistrationManagement } from "../components/CompanyRegistrationManagement";
import { adminAPI } from "../../../services/api";
import { ordersAPI } from "../../../services/api";
import { useNavigate } from "react-router-dom";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export function AdminPage() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [selectedLog] = useState<any>(null);
  const [logDetailDialog, setLogDetailDialog] = useState(false);
  const [systemStatus, setSystemStatus] = useState<any>(null);
  const [systemStatusLoading, setSystemStatusLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const {
    company,
    settings,
    users,
    isLoadingCompany,
    isLoadingSettings,
    isLoadingUsers,
    isUpdatingCompany,
    isCreatingUser,
    isUpdatingUser,
    isDeletingUser,
    isUpdatingSettings,
    error,
    updateCompany,
    createUser,
    updateUser,
    deleteUser,
    updateSettings,
    refetchCompany,
    refetchSettings,
  } = useAdmin();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const loadSystemStatus = async () => {
    try {
      setSystemStatusLoading(true);
      const response = await adminAPI.getSystemStatus();
      setSystemStatus(response);
    } catch (error) {
      console.error("Failed to load system status:", error);
    } finally {
      setSystemStatusLoading(false);
    }
  };

  const loadOrders = useCallback(async () => {
    try {
      setOrdersLoading(true);
      const response = await ordersAPI.getOrders();
      setOrders(response.orders || []);
    } catch (error) {
      console.error("Failed to load orders:", error);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSystemStatus();
    if (tabValue === 5) {
      // Sipariş Yönetimi sekmesi
      loadOrders();
    }
  }, [tabValue, loadOrders]);

  const formatLogDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("tr-TR");
  };

  return (
    <DashboardLayout
      title="Yönetim Paneli"
      breadcrumbs={[{ label: "Yönetim" }]}
    >
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="admin tabs"
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            <Tab
              icon={<BusinessIcon />}
              label="Şirket Bilgileri"
              iconPosition="start"
            />
            <Tab
              icon={<PeopleIcon />}
              label="Kullanıcı Yönetimi"
              iconPosition="start"
            />
            <Tab
              icon={<SettingsIcon />}
              label="Sistem Ayarları"
              iconPosition="start"
            />
            <Tab
              icon={<MonitorIcon />}
              label="Sistem Durumu"
              iconPosition="start"
            />
            <Tab
              icon={<AssessmentIcon />}
              label="Sistem Logları"
              iconPosition="start"
            />
            <Tab
              icon={<ShoppingCartIcon />}
              label="Sipariş Yönetimi"
              iconPosition="start"
            />
            <Tab
              icon={<AddBusinessIcon />}
              label="Şirket Kayıt Yönetimi"
              iconPosition="start"
            />
          </Tabs>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Company Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <CompanyManagement
            company={company}
            isLoading={isLoadingCompany}
            isUpdating={isUpdatingCompany}
            error={error}
            onUpdate={updateCompany}
            onRefresh={refetchCompany}
          />
        </TabPanel>

        {/* User Management Tab */}
        <TabPanel value={tabValue} index={1}>
          <UserManagement
            users={users?.users}
            isLoading={isLoadingUsers}
            isCreating={isCreatingUser}
            isUpdating={isUpdatingUser}
            isDeleting={isDeletingUser}
            error={error}
            onCreate={createUser}
            onUpdate={updateUser}
            onDelete={deleteUser}
          />
        </TabPanel>

        {/* System Settings Tab */}
        <TabPanel value={tabValue} index={2}>
          <SystemSettingsComponent
            settings={settings}
            isLoading={isLoadingSettings}
            isUpdating={isUpdatingSettings}
            error={error}
            onUpdate={updateSettings}
            onRefresh={refetchSettings}
          />
        </TabPanel>

        {/* System Status Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            {/* System Overview */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Sistem Genel Durumu"
                  action={
                    <Button
                      startIcon={<RefreshIcon />}
                      variant="outlined"
                      size="small"
                    >
                      Yenile
                    </Button>
                  }
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "success.light", color: "white" }}>
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="h4">
                                {systemStatusLoading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  `%${systemStatus?.system_health || 0}`
                                )}
                              </Typography>
                              <Typography variant="body2">
                                Sistem Sağlığı
                              </Typography>
                            </Box>
                            <CheckCircle sx={{ fontSize: 40 }} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "info.light", color: "white" }}>
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="h4">
                                {systemStatusLoading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  `${systemStatus?.average_response_time || 0}s`
                                )}
                              </Typography>
                              <Typography variant="body2">
                                Ortalama Yanıt
                              </Typography>
                            </Box>
                            <SpeedIcon sx={{ fontSize: 40 }} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "warning.light", color: "white" }}>
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="h4">
                                {systemStatusLoading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  `%${systemStatus?.cpu_usage || 0}`
                                )}
                              </Typography>
                              <Typography variant="body2">
                                CPU Kullanımı
                              </Typography>
                            </Box>
                            <MemoryIcon sx={{ fontSize: 40 }} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card sx={{ bgcolor: "secondary.light", color: "white" }}>
                        <CardContent>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Box>
                              <Typography variant="h4">
                                {systemStatusLoading ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  `%${systemStatus?.ram_usage || 0}`
                                )}
                              </Typography>
                              <Typography variant="body2">
                                RAM Kullanımı
                              </Typography>
                            </Box>
                            <StorageIcon sx={{ fontSize: 40 }} />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Active Users */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Aktif Kullanıcılar" />
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: "primary.main" }}>A</Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Admin User"
                        secondary="Son aktivite: 2 dakika önce"
                      />
                      <Chip label="Aktif" color="success" size="small" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: "secondary.main" }}>M</Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Manager User"
                        secondary="Son aktivite: 5 dakika önce"
                      />
                      <Chip label="Aktif" color="success" size="small" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: "info.main" }}>S</Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary="Sales Person"
                        secondary="Son aktivite: 10 dakika önce"
                      />
                      <Chip label="Aktif" color="success" size="small" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* System Alerts */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Sistem Uyarıları" />
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Yüksek CPU Kullanımı"
                        secondary="CPU kullanımı %80'in üzerinde"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <InfoIcon color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Yedekleme Tamamlandı"
                        secondary="Sistem yedeği başarıyla oluşturuldu"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircle color="success" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Veritabanı Bağlantısı"
                        secondary="Veritabanı bağlantısı stabil"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Network Status */}
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Ağ Durumu" />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <NetworkIcon color="success" sx={{ fontSize: 40 }} />
                        <Typography variant="h6">API Sunucusu</Typography>
                        <Chip label="Çevrimiçi" color="success" size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <StorageIcon color="success" sx={{ fontSize: 40 }} />
                        <Typography variant="h6">Veritabanı</Typography>
                        <Chip label="Çevrimiçi" color="success" size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <MemoryIcon color="success" sx={{ fontSize: 40 }} />
                        <Typography variant="h6">Cache Sunucusu</Typography>
                        <Chip label="Çevrimiçi" color="success" size="small" />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box textAlign="center">
                        <SpeedIcon color="success" sx={{ fontSize: 40 }} />
                        <Typography variant="h6">CDN</Typography>
                        <Chip label="Çevrimiçi" color="success" size="small" />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* System Logs Tab */}
        <TabPanel value={tabValue} index={4}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sistem Logları
            </Typography>
            {/* Logs content */}
          </Box>
        </TabPanel>

        {/* Orders Management Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sipariş Yönetimi
            </Typography>
            {ordersLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sipariş No</TableCell>
                      <TableCell>Müşteri</TableCell>
                      <TableCell>Toplam</TableCell>
                      <TableCell>Durum</TableCell>
                      <TableCell>Tarih</TableCell>
                      <TableCell>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>{order.order_number}</TableCell>
                        <TableCell>{order.customer_name}</TableCell>
                        <TableCell>
                          {order.total_amount?.toFixed(2)} TL
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            color={
                              order.status === "pending_approval"
                                ? "warning"
                                : "default"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(order.created_at).toLocaleDateString(
                            "tr-TR"
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/orders/${order.id}`)}
                            title="Detayları Görüntüle"
                          >
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Company Registration Management Tab */}
        <TabPanel value={tabValue} index={6}>
          <CompanyRegistrationManagement />
        </TabPanel>
      </Box>

      {/* Log Detail Dialog */}
      <Dialog
        open={logDetailDialog}
        onClose={() => setLogDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Log Detayları
          <IconButton
            onClick={() => setLogDetailDialog(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLog && (
            <List>
              <ListItem>
                <ListItemIcon>
                  {selectedLog.level === "info" ? (
                    <InfoIcon />
                  ) : selectedLog.level === "warning" ? (
                    <WarningIcon />
                  ) : selectedLog.level === "error" ? (
                    <ErrorIcon />
                  ) : (
                    <InfoIcon />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary="Seviye"
                  secondary={selectedLog.level.toUpperCase()}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Mesaj" secondary={selectedLog.message} />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Kullanıcı"
                  secondary={selectedLog.user_name}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="IP Adresi"
                  secondary={selectedLog.ip_address}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="User Agent"
                  secondary={selectedLog.user_agent}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Tarih"
                  secondary={formatLogDate(selectedLog.timestamp)}
                />
              </ListItem>
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDetailDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
